import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Search, Filter, Eye, Download, Trash2, Lock, Globe, ArrowLeft, Clock, Shield, User, FolderInput } from 'lucide-react';
import { formatFileSize } from '../mockData';
import FileViewerDialog from './FileViewerDialog';
import FileAccessManager from './FileAccessManager';
import { useToast } from '../hooks/use-toast';
import { filesApi } from '../lib/api';

const FileList = ({ files, user, directories, onDeleteFile, onUpdateFile, onRequestAccess, selectedDirectory, onBackToAll, isTrash, onRestoreFile, onMoveFile }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [manageAccess, setManageAccess] = useState(null);
  const [moveFile, setMoveFile] = useState(null);
  const [targetDirectoryId, setTargetDirectoryId] = useState('');
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(null);
  const { toast } = useToast();

  const isOwner = (file) => user && file.userId === user.id;
  const isAdmin = user && (user.is_admin === 1 || user.is_admin === true || user.is_admin === '1');

  /* Diagnostic logs removed */

  const canDownload = (file) => {
    // Owner and Admin always have access
    if (isOwner(file) || isAdmin) return true;

    // A private file can be downloaded if access request is approved
    if (file.accessStatus === 'approved') return true;

    // If file explicitly restricts download, even if public, only owner/admin/approved can download
    if (file.allowDownload === false) return false;

    // For files that allow download:
    // 1. If public -> anyone can download
    if (file.isPublic) return true;

    // 2. If user has 'view_all' permission -> they can download private files that allow download
    // 2. If user has 'view_all' permission -> they can download private files that allow download
    if (user && (user.can_view_all === 1 || user.can_view_all === true || user.can_view_all === '1')) return true;

    return false;
  };

  const getDirectoryName = (dirId) => {
    const dir = directories.find(d => d.id === dirId);
    return dir?.name || 'Unknown';
  };

  const formatDate = (value) => {
    if (!value) return 'Unknown';
    let date;
    if (typeof value === 'string') {
      // Try parse ISO or numeric string
      const num = Number(value);
      if (!Number.isNaN(num)) {
        date = new Date(num * (num < 10000000000 ? 1000 : 1));
      } else {
        date = new Date(value);
      }
    } else if (typeof value === 'number') {
      date = new Date(value * (value < 10000000000 ? 1000 : 1));
    } else {
      return 'Unknown';
    }
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || file.contentType.startsWith(filterType);
    const matchesVisibility = filterVisibility === 'all' ||
      (filterVisibility === 'public' && file.isPublic) ||
      (filterVisibility === 'private' && !file.isPublic);

    return matchesSearch && matchesType && matchesVisibility;
  });

  const handleDownload = async (file) => {
    try {
      const { filesApi } = await import('../lib/api');
      const blob = await filesApi.download(file.id);
      if (!blob) throw new Error('No content received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download failed',
        description: error.message || 'Failed to download file.',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (file) => {
    setDeleting(true);
    try {
      const { filesApi } = await import('../lib/api');
      await filesApi.delete(file.id);
      onDeleteFile(file.id);
      toast({
        title: 'File deleted',
        description: `${file.name} has been deleted.`
      });
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: 'Delete failed',
        description: error.message || 'Failed to delete file.',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleMove = async () => {
    if (!moveFile) return;
    setMoving(true);
    try {
      const { filesApi } = await import('../lib/api');
      // If targetDirectoryId is 'root' or empty string, send null
      const dirId = targetDirectoryId === 'root' || targetDirectoryId === '' ? null : targetDirectoryId;

      await filesApi.move(moveFile.id, dirId);
      onMoveFile(moveFile.id, dirId);

      const dirName = dirId ? getDirectoryName(dirId) : 'Root';
      toast({
        title: 'File moved',
        description: `${moveFile.name} moved to ${dirName}.`
      });
    } catch (error) {
      console.error('Move failed:', error);
      toast({
        title: 'Move failed',
        description: error.response?.data?.error || error.message || 'Failed to move file.',
        variant: 'destructive'
      });
    } finally {
      setMoving(false);
      setMoveFile(null);
      setTargetDirectoryId('');
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedDirectory ? selectedDirectory.name : 'All Files'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'}
            </p>
          </div>
          {selectedDirectory && (
            <Button
              onClick={onBackToAll}
              variant="outline"
              size="sm"
              className="border-gray-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to All Files
            </Button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="application/pdf">PDFs</SelectItem>
              <SelectItem value="application">Documents</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterVisibility} onValueChange={setFilterVisibility}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Visibility</SelectItem>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filteredFiles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No files found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Directory</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibility</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auto-Delete</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFiles.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      {(() => {
                        const isImage = (file.contentType || '').startsWith('image/');
                        const thumbSrc = isImage ? (file.url || (file.isPublic ? filesApi.getPreviewUrl(file.id) : null)) : null;
                        const canShow = (file.isPublic || isOwner(file) || isAdmin || file.accessStatus === 'approved') && !!thumbSrc;
                        return canShow ? (
                          <img src={thumbSrc} alt={file.name} className="w-10 h-10 object-cover rounded mr-3" />
                        ) : null;
                      })()}
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-gray-500">{file.contentType?.split('/')[1]}</p>
                          {(file.uploaderName || file.uploaderUsername) && (
                            <div className="flex items-center gap-1 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm">
                              <User className="h-2.5 w-2.5" />
                              <span>{file.uploaderName || `@${file.uploaderUsername}`}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{getDirectoryName(file.directoryId)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(file.uploaded_at ?? file.uploadedAt)}</td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      title={file.isLocked && !isAdmin ? "Locked by Administrator" : "Change Visibility"}
                      disabled={(!isAdmin && (file.isLocked || !isOwner(file) || file.isPublic))}
                      onClick={async () => {
                        const newVisibility = !file.isPublic;
                        try {
                          const { filesApi } = await import('../lib/api');
                          await filesApi.updateVisibility(file.id, newVisibility);
                          onUpdateFile({ ...file, isPublic: newVisibility });
                          toast({
                            title: newVisibility ? 'File is now public' : 'File is now private',
                            description: `${file.name} visibility updated.`
                          });
                        } catch (error) {
                          toast({
                            title: 'Update failed',
                            description: 'Failed to update file visibility.',
                            variant: 'destructive'
                          });
                        }
                      }}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium h-auto ${file.isPublic
                        ? 'bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-900'
                        : 'bg-orange-100 text-orange-800 hover:bg-orange-200 hover:text-orange-900'
                        }`}
                    >
                      {file.isPublic ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Public
                        </span>
                      ) : (
                        <div className="flex flex-col items-start gap-1">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Private
                          </span>
                          {file.isLocked && (
                            <span className="text-[10px] text-amber-600 font-medium flex items-center">
                              <Lock className="h-2.5 w-2.5 mr-0.5" /> Locked by Admin
                            </span>
                          )}
                        </div>
                      )}
                    </Button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-3 w-3 mr-1" />
                      {file.autoDeleteIn}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      {isTrash ? (
                        <Button
                          size="sm"
                          title="Restore File"
                          variant="ghost"
                          onClick={() => onRestoreFile(file.id)}
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw h-4 w-4"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74-2.74L3 12" /><path d="M3 3v9h9" /></svg>
                          <span className="ml-2">Restore</span>
                        </Button>
                      ) : (
                        <>
                          {canDownload(file) ? (
                            <>
                              <Button
                                size="sm"
                                title="View File"
                                variant="ghost"
                                onClick={() => setViewFile(file)}
                                className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                title="Download"
                                variant="ghost"
                                onClick={() => handleDownload(file)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            // Never show Request Access to owner or admin
                            !isOwner(file) && !isAdmin ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={file.accessStatus === 'pending' || loadingRequest === file.id}
                                onClick={async () => {
                                  setLoadingRequest(file.id);
                                  await onRequestAccess(file.id);
                                  setLoadingRequest(null);
                                }}
                                className={`${file.accessStatus === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'text-teal-600 border-teal-200 hover:bg-teal-50'}`}
                              >
                                {file.accessStatus === 'pending' ? 'Pending Approval' : 'Request Access'}
                              </Button>
                            ) : null
                          )}

                          {(isOwner(file) || isAdmin) && (
                            <Button
                              size="sm"
                              title="Manage Access"
                              variant="ghost"
                              onClick={() => setManageAccess(file)}
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                            >
                              <Shield className="h-4 w-4" />
                            </Button>
                          )}

                          {(isAdmin ||
                            (isOwner(file) && user?.can_delete) ||
                            ((file.allowDelete || file.accessStatus === 'approved') && user?.can_delete)) && (
                              <Button
                                size="sm"
                                title="Delete"
                                variant="ghost"
                                onClick={() => setDeleteConfirm(file)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}

                          {(isAdmin || isOwner(file)) && (
                            <Button
                              size="sm"
                              title="Move to Folder"
                              variant="ghost"
                              onClick={() => {
                                setMoveFile(file);
                                setTargetDirectoryId(file.directoryId || 'root');
                              }}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            >
                              <FolderInput className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      }

      {deleteConfirm && (
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete File</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {moveFile && (
        <Dialog open={!!moveFile} onOpenChange={() => setMoveFile(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Move File</DialogTitle>
              <DialogDescription>
                Select the destination folder for "{moveFile.name}".
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium mb-2 block">Destination Folder</label>
              <Select value={targetDirectoryId} onValueChange={setTargetDirectoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">Root (No Folder)</SelectItem>
                  {directories.map(dir => (
                    <SelectItem key={dir.id} value={dir.id}>
                      {dir.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveFile(null)}>Cancel</Button>
              <Button onClick={handleMove} disabled={moving}>
                {moving ? 'Moving...' : 'Move'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {viewFile && (
        <FileViewerDialog
          file={viewFile}
          open={!!viewFile}
          onClose={() => setViewFile(null)}
          onDownload={() => handleDownload(viewFile)}
        />
      )}

      {manageAccess && (
        <FileAccessManager
          file={manageAccess}
          open={!!manageAccess}
          onClose={() => setManageAccess(null)}
        />
      )}
    </Card>
  );
};

export default FileList;
